export interface TooltipPosition {
  top: number;
  left: number;
  transform: string;
  transformOrigin: string;
  width?: number; // Add width property
}

export interface TooltipDimensions {
  width: number;
  height: number;
}

export interface ViewportBounds {
  width: number;
  height: number;
  top: number;
  left: number;
}

export interface PositionOptions {
  side: "top" | "right" | "bottom" | "left";
  align: "start" | "center" | "end";
  sideOffset: number;
  alignOffset: number;
}

/**
 * Calculates the optimal position for a tooltip to ensure it stays within viewport bounds
 */
export function calculateTooltipPosition(
  triggerRect: DOMRect,
  tooltipDimensions: TooltipDimensions,
  containerRect: DOMRect,
  viewportBounds: ViewportBounds,
  options: PositionOptions
): TooltipPosition {
  // Calculate initial position based on preferred side and alignment
  let position = calculateInitialPosition(
    triggerRect,
    containerRect,
    options
  );

  // Check if tooltip would overflow viewport and adjust if necessary
  const adjustedPosition = adjustForViewportBounds(
    position,
    tooltipDimensions,
    containerRect,
    viewportBounds,
    triggerRect,
    options
  );

  return { ...adjustedPosition, width: triggerRect.width }; // Pass trigger width
}

/**
 * Calculates the initial position based on side and alignment preferences
 */
function calculateInitialPosition(
  triggerRect: DOMRect,
  containerRect: DOMRect,
  options: PositionOptions
): TooltipPosition {
  const { side, align, sideOffset, alignOffset } = options;
  
  let top = 0;
  let left = 0;
  let transform = "";
  let transformOrigin = "center center";

  // Calculate base position relative to trigger
  const triggerCenterX = triggerRect.left - containerRect.left + triggerRect.width / 2;
  const triggerCenterY = triggerRect.top - containerRect.top + triggerRect.height / 2;

  switch (side) {
    case "top":
      top = triggerRect.top - containerRect.top - sideOffset;
      left = align === "start" ? triggerRect.left - containerRect.left : triggerCenterX; // Adjust left for 'start' alignment
      transform = getTransformForAlignment("top", align);
      transformOrigin = "center bottom";
      break;
    case "bottom":
      top = triggerRect.bottom - containerRect.top + sideOffset;
      left = align === "start" ? triggerRect.left - containerRect.left : triggerCenterX; // Adjust left for 'start' alignment
      transform = getTransformForAlignment("bottom", align);
      transformOrigin = "center top";
      break;
    case "left":
      top = triggerCenterY;
      left = triggerRect.left - containerRect.left - sideOffset;
      transform = getTransformForAlignment("left", align);
      transformOrigin = "right center";
      break;
    case "right":
      top = triggerCenterY;
      left = triggerRect.right - containerRect.left + sideOffset;
      transform = getTransformForAlignment("right", align);
      transformOrigin = "left center";
      break;
  }

  // Apply alignment offset
  if (side === "top" || side === "bottom") {
    // Only apply alignOffset if not 'start' alignment, as 'start' is already aligned to left edge
    if (align !== "start") {
      left += alignOffset;
    }
  } else {
    top += alignOffset;
  }

  return { top, left, transform, transformOrigin };
}

/**
 * Gets the appropriate transform string for the given side and alignment
 */
function getTransformForAlignment(side: string, align: string): string {
  const transforms: string[] = [];

  if (side === "top") {
    transforms.push("translateY(-100%)");
  } else if (side === "left") {
    transforms.push("translateX(-100%)");
  }

  if (side === "top" || side === "bottom") {
    switch (align) {
      case "start":
        transforms.push("translateX(0%)");
        break;
      case "center":
        transforms.push("translateX(-50%)");
        break;
      case "end":
        transforms.push("translateX(-100%)");
        break;
    }
  } else {
    switch (align) {
      case "start":
        transforms.push("translateY(0%)");
        break;
      case "center":
        transforms.push("translateY(-50%)");
        break;
      case "end":
        transforms.push("translateY(-100%)");
        break;
    }
  }

  return transforms.join(" ");
}

/**
 * Adjusts tooltip position to ensure it stays within viewport bounds
 */
function adjustForViewportBounds(
  position: TooltipPosition,
  tooltipDimensions: TooltipDimensions,
  containerRect: DOMRect,
  viewportBounds: ViewportBounds,
  triggerRect: DOMRect,
  options: PositionOptions
): TooltipPosition {
  const { side } = options;
  let { top, left, transform, transformOrigin } = position;

  // Calculate tooltip bounds in viewport coordinates
  const containerInViewport = {
    left: containerRect.left - viewportBounds.left,
    top: containerRect.top - viewportBounds.top,
  };

  const tooltipInViewport = {
    left: containerInViewport.left + left,
    top: containerInViewport.top + top,
  };

  // Apply current transform to get actual tooltip position
  const actualBounds = calculateActualBounds(
    tooltipInViewport,
    tooltipDimensions,
    transform
  );

  // Check for viewport overflow and adjust
  const overflow = {
    left: Math.max(0, viewportBounds.left - actualBounds.left),
    right: Math.max(0, actualBounds.right - (viewportBounds.left + viewportBounds.width)),
    top: Math.max(0, viewportBounds.top - actualBounds.top),
    bottom: Math.max(0, actualBounds.bottom - (viewportBounds.top + viewportBounds.height)),
  };

  // Adjust position based on overflow
  if (overflow.left > 0) {
    left += overflow.left / containerRect.width * containerRect.width;
  }
  if (overflow.right > 0) {
    left -= overflow.right / containerRect.width * containerRect.width;
  }
  if (overflow.top > 0) {
    top += overflow.top / containerRect.height * containerRect.height;
  }
  if (overflow.bottom > 0) {
    top -= overflow.bottom / containerRect.height * containerRect.height;
  }

  // If there's significant overflow, try flipping to opposite side
  const shouldFlipVertically = (side === "top" && overflow.top > tooltipDimensions.height / 2) ||
                              (side === "bottom" && overflow.bottom > tooltipDimensions.height / 2);
  
  const shouldFlipHorizontally = (side === "left" && overflow.left > tooltipDimensions.width / 2) ||
                                (side === "right" && overflow.right > tooltipDimensions.width / 2);

  if (shouldFlipVertically || shouldFlipHorizontally) {
    const flippedSide = getFlippedSide(side);
    const flippedOptions = { ...options, side: flippedSide };
    
    // Recursively calculate position with flipped side
    const flippedPosition = calculateInitialPosition(
      triggerRect,
      containerRect,
      flippedOptions
    );

    // Check if flipped position is better
    const flippedActualBounds = calculateActualBounds(
      {
        left: containerInViewport.left + flippedPosition.left,
        top: containerInViewport.top + flippedPosition.top,
      },
      tooltipDimensions,
      flippedPosition.transform
    );

    const flippedOverflow = {
      left: Math.max(0, viewportBounds.left - flippedActualBounds.left),
      right: Math.max(0, flippedActualBounds.right - (viewportBounds.left + viewportBounds.width)),
      top: Math.max(0, viewportBounds.top - flippedActualBounds.top),
      bottom: Math.max(0, flippedActualBounds.bottom - (viewportBounds.top + viewportBounds.height)),
    };

    const currentOverflowArea = overflow.left + overflow.right + overflow.top + overflow.bottom;
    const flippedOverflowArea = flippedOverflow.left + flippedOverflow.right + flippedOverflow.top + flippedOverflow.bottom;

    if (flippedOverflowArea < currentOverflowArea) {
      return flippedPosition;
    }
  }

  return { top, left, transform, transformOrigin };
}

/**
 * Calculates the actual bounds of the tooltip after applying transforms
 */
function calculateActualBounds(
  position: { left: number; top: number },
  dimensions: TooltipDimensions,
  transform: string
): { left: number; top: number; right: number; bottom: number } {
  let { left, top } = position;
  const { width, height } = dimensions;

  // Parse transform to adjust position
  if (transform.includes("translateX(-100%)")) {
    left -= width;
  } else if (transform.includes("translateX(-50%)")) {
    left -= width / 2;
  }

  if (transform.includes("translateY(-100%)")) {
    top -= height;
  } else if (transform.includes("translateY(-50%)")) {
    top -= height / 2;
  }

  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
  };
}

/**
 * Gets the opposite side for flipping
 */
function getFlippedSide(side: string): "top" | "right" | "bottom" | "left" {
  switch (side) {
    case "top": return "bottom";
    case "bottom": return "top";
    case "left": return "right";
    case "right": return "left";
    default: return "top";
  }
}

/**
 * Gets viewport bounds, accounting for portal root if provided
 */
export function getViewportBounds(portalRoot?: HTMLElement | null): ViewportBounds {
  if (portalRoot) {
    const rect = portalRoot.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      top: rect.top,
      left: rect.left,
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
    top: 0,
    left: 0,
  };
}

/**
 * Estimates tooltip dimensions before it's rendered
 * This is a rough estimate - actual dimensions may vary
 */
export function estimateTooltipDimensions(content: string): TooltipDimensions {
  // Create a temporary element to measure text
  const temp = document.createElement("div");
  temp.style.position = "absolute";
  temp.style.visibility = "hidden";
  temp.style.whiteSpace = "nowrap";
  temp.style.fontSize = "14px"; // Assuming text-sm
  temp.style.padding = "6px 12px"; // Assuming px-3 py-1.5
  temp.style.maxWidth = "320px"; // Assuming max-w-xs
  temp.textContent = content;
  
  document.body.appendChild(temp);
  const rect = temp.getBoundingClientRect();
  document.body.removeChild(temp);

  return {
    width: Math.min(rect.width, 320), // Respect max-width
    height: rect.height,
  };
}
